/**
 * Browser Notification Service
 * Handles requesting permission and showing browser notifications
 */

export interface NotificationOptions {
  title: string
  body: string
  icon?: string
  badge?: string
  tag?: string
  data?: any
  requireInteraction?: boolean
  silent?: boolean
}

class NotificationService {
  private permission: NotificationPermission = 'default'
  private isSupported: boolean = false

  constructor() {
    this.isSupported = 'Notification' in window
    if (this.isSupported) {
      this.permission = Notification.permission
    }
  }

  /**
   * Request notification permission from the user
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported) {
      console.warn('Browser notifications are not supported')
      return 'denied'
    }

    // Update permission status (it might have changed)
    this.permission = Notification.permission

    if (this.permission === 'granted') {
      return 'granted'
    }

    if (this.permission === 'denied') {
      console.warn('Notification permission was denied. User needs to enable it in browser settings.')
      return 'denied'
    }

    try {
      // Only request if permission is 'default'
      const permission = await Notification.requestPermission()
      this.permission = permission
      
      if (permission === 'granted') {
        console.log('Notification permission granted')
      } else if (permission === 'denied') {
        console.warn('Notification permission was denied by user')
      }
      
      return permission
    } catch (error) {
      console.error('Error requesting notification permission:', error)
      return 'denied'
    }
  }

  /**
   * Check if notifications are supported and allowed
   */
  canNotify(): boolean {
    return this.isSupported && this.permission === 'granted'
  }

  /**
   * Show a browser notification
   */
  async showNotification(options: NotificationOptions): Promise<Notification | null> {
    if (!this.canNotify()) {
      // Try to request permission if not granted
      const permission = await this.requestPermission()
      if (permission !== 'granted') {
        console.warn('Cannot show notification: permission not granted')
        return null
      }
    }

    try {
      const notificationOptions: NotificationOptions = {
        icon: options.icon || '/LOGO-Icon.png',
        badge: options.badge || '/LOGO-Icon.png',
        tag: options.tag,
        data: options.data,
        requireInteraction: options.requireInteraction || false,
        silent: options.silent || false,
        ...options
      }

      const notification = new Notification(options.title, notificationOptions)

      // Auto-close after 5 seconds (unless requireInteraction is true)
      if (!notificationOptions.requireInteraction) {
        setTimeout(() => {
          notification.close()
        }, 5000)
      }

      // Handle click to focus window
      notification.onclick = (event) => {
        event.preventDefault()
        window.focus()
        notification.close()
        
        // If data contains a URL or route, navigate to it
        if (options.data?.url) {
          window.location.href = options.data.url
        } else if (options.data?.route) {
          // Use React Router if available
          const event = new CustomEvent('navigate', { detail: options.data.route })
          window.dispatchEvent(event)
        }
      }

      return notification
    } catch (error) {
      console.error('Error showing notification:', error)
      return null
    }
  }

  /**
   * Show a notification for an issue comment
   */
  async notifyComment(
    issueKey: string,
    _issueTitle: string,
    commentAuthor: string,
    commentPreview: string,
    issueUrl?: string
  ): Promise<Notification | null> {
    return this.showNotification({
      title: `New comment on ${issueKey}`,
      body: `${commentAuthor} commented: ${commentPreview.substring(0, 100)}${commentPreview.length > 100 ? '...' : ''}`,
      tag: `comment-${issueKey}`,
      data: {
        type: 'comment',
        issueKey,
        url: issueUrl
      },
      requireInteraction: false
    })
  }

  /**
   * Show a notification for an issue status change
   */
  async notifyStatusChange(
    issueKey: string,
    _issueTitle: string,
    oldStatus: string,
    newStatus: string,
    changedBy: string,
    issueUrl?: string
  ): Promise<Notification | null> {
    return this.showNotification({
      title: `Status changed: ${issueKey}`,
      body: `${changedBy} changed status from "${oldStatus}" to "${newStatus}"`,
      tag: `status-${issueKey}`,
      data: {
        type: 'status_change',
        issueKey,
        url: issueUrl
      },
      requireInteraction: false
    })
  }

  /**
   * Show a notification for an issue assignment
   */
  async notifyAssignment(
    issueKey: string,
    issueTitle: string,
    assignedBy: string,
    issueUrl?: string
  ): Promise<Notification | null> {
    return this.showNotification({
      title: `Assigned to you: ${issueKey}`,
      body: `${assignedBy} assigned "${issueTitle}" to you`,
      tag: `assignment-${issueKey}`,
      data: {
        type: 'assignment',
        issueKey,
        url: issueUrl
      },
      requireInteraction: true
    })
  }

  /**
   * Show a notification for an issue update
   */
  async notifyUpdate(
    issueKey: string,
    _issueTitle: string,
    updatedBy: string,
    changeDescription: string,
    issueUrl?: string
  ): Promise<Notification | null> {
    return this.showNotification({
      title: `Updated: ${issueKey}`,
      body: `${updatedBy} ${changeDescription}`,
      tag: `update-${issueKey}`,
      data: {
        type: 'update',
        issueKey,
        url: issueUrl
      },
      requireInteraction: false
    })
  }

  /**
   * Show a notification for a new issue
   */
  async notifyNewIssue(
    issueKey: string,
    issueTitle: string,
    createdBy: string,
    issueUrl?: string
  ): Promise<Notification | null> {
    return this.showNotification({
      title: `New issue: ${issueKey}`,
      body: `${createdBy} created "${issueTitle}"`,
      tag: `new-${issueKey}`,
      data: {
        type: 'new_issue',
        issueKey,
        url: issueUrl
      },
      requireInteraction: false
    })
  }
}

// Export singleton instance
export const notificationService = new NotificationService()

// Request permission on module load (non-blocking)
if (typeof window !== 'undefined') {
  // Request permission after a short delay to avoid blocking page load
  // Only request if permission is 'default' (not already granted or denied)
  setTimeout(() => {
    if (Notification.permission === 'default') {
      notificationService.requestPermission().catch(console.error)
    }
  }, 2000) // Increased delay to 2 seconds to let page load first
}

